import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { formatAddress } from '../../../helper'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { useWindowBreakPoint } from '../../../hooks/useWindowBreakPoint'
import { setAutocompleteGeo, setCustomer, setCustomerAddressIsNotFull, setCustomerGeoData, setCustomerGeoDataLoading, setCustomersByNumber, setCustomersByPhone } from '../../../redux/action/customer'
import { setDeliveryCartCurrentDeliveryCost, setDeliveryCartItemFocusIndex, setDeliveryCartItems, setDeliveryCartOrderType } from '../../../redux/action/deliveryCart'
import { setAppEventZIndex } from '../../../redux/action/system'
import { selectCompanyData, selectIsTestCompany } from '../../../redux/selector/system'
import { deliveryDistanceCustomer, distanceCustomer, searchCustomersByNumberThunk, searchCustomersByPhoneThunk, searchResetThunk } from '../../../redux/thunk/customer'
import { getDeliveryConfigThunk, resetDeliveryConfigThunk } from '../../../redux/thunk/deliveryConfig'
import { theme } from '../../../style/theme'
import Button from '../../Elements/buttons/Button'
import { FormButtons } from '../../Elements/form/index.styled'
import AddressSearchInputs from '../../Elements/inputs/AddressSearchInputs'
import AddressSearchInputsV2 from '../../Elements/inputs/AddressSearchInputsV2'
import AutocompleteInput from '../../Elements/inputs/AutocompleteInput'
import CheckBoxInputControlledGray from '../../Elements/inputs/CheckBoxInputGray/controlled'
import TextInput from '../../Elements/inputs/TextInput'
import { NumberAutocompleteItem, PhoneAutocompleteItem } from './autocomplete/items'
import { BlockShowForm, CustomerAddressWrapper, CustomerFormWrapper, CustomerMainForm, CustomerPhoneNumberWrapper, FormLine } from './index.styled'
import keyboardImg from '../../../img/svg/input/keyboard.svg'
import CustomerNameInputs from './name'

const EVENT_Z_INDEX = 0
const PHONE_EVENT_Z_INDEX = 9
const NUMBER_EVENT_Z_INDEX = 10

const CustomerForm = React.memo(({ onSubmit, handleSubmit, register, errors, handleReset, setValue, watch, setFocus, formRef, clearErrors }) => {
	const customer = useSelector(store => store.customerStore.customer)
	const deliveryCost = useSelector(store => store.customerStore.deliveryCost)
	const geoData = useSelector(store => store.customerStore.geoData)
	const customersByPhone = useSelector(store => store.customerStore.customersByPhone)
	const customersByNumber = useSelector(store => store.customerStore.customersByNumber)
	const geoDataLoading = useSelector(store => store.customerStore.geoDataLoading)
	const customerAddressIsNotFull = useSelector(store => store.customerStore.customerAddressIsNotFull)
	const [alwaysShowForm, setAlwaysShowForm] = useLocalStorage('showForm', true)
	const [showForm, setShowForm] = useState(true)
	const [showSuggest, setShowSuggest] = useState(false)
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const isTablet = useWindowBreakPoint(theme.point820)
	const companyContryCode = useSelector(selectCompanyData('countryCode'))
	const watchAddressHouse = watch('customerAddress.house')
	const watchAddressStreet = watch('customerAddress.street')
	const watchAddressCity = watch('customerAddress.city')
	const watchAddressZipCode = watch('customerAddress.zipCode')
	const watchDeliveryCost = watch('deliveryCost')
	const isValidAddress = watchAddressStreet && watchAddressHouse && watchAddressCity && watchAddressZipCode
	const orderType = useSelector(store => store.deliveryCart.orderType)
	const validationIsRequired = !['PICKUP', 'COUNTER'].includes(orderType)
	const isTestCompany = useSelector(selectIsTestCompany)

	const customerDistance = (geoData?.distance || 0) / 1000.0
	const customerLat = geoData?.lat
	const customerLon = geoData?.lon

	const phoneInputRef = useRef(null)
	const { ref: registerPhoneNumberRef } = register('phoneNumber')
	const [phoneInputMode, setPhoneInputMode] = useState('none')

	useEffect(() => {
		dispatch(setAppEventZIndex(EVENT_Z_INDEX))
		dispatch(setDeliveryCartOrderType(null))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowForm(alwaysShowForm)
	}, [alwaysShowForm])

	useEffect(() => {
		if (deliveryCost) setValue('deliveryCost', deliveryCost)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deliveryCost])

	useEffect(() => {
		return () => {
			if (phoneInputRef?.current) {
				setPhoneInputMode('none')
				phoneInputRef.current.blur()
			}
		}
	}, [])

	const setDeliveryCost = useCallback((deliveryConfig) => {
		const isDeliveryCostNotSet = customer ? deliveryCost == null : deliveryCost === 0
		if (isDeliveryCostNotSet && deliveryConfig.deliveryCost) {
			setValue('deliveryCost', deliveryConfig.deliveryCost)
		}
	}, [customer, deliveryCost, setValue])

	useEffect(() => {
		if (customerDistance >= 0 && customerLat && customerLon) {
			dispatch(getDeliveryConfigThunk(customerDistance, customerLat, customerLon, setDeliveryCost))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerDistance, customerLat, customerLon])

	useEffect(() => {
		if (geoData) {
			setValue('customerAddress.latitude', geoData.lat)
			setValue('customerAddress.longitude', geoData.lon)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [geoData])

	useEffect(() => {
		if (isValidAddress && !customerAddressIsNotFull) {
			dispatch(setCustomerGeoData(null)) // should reset geoData, cause of coordinates change, so it contains deprecated values
			dispatch(setCustomerGeoDataLoading(true))
			const timeOutId = setTimeout(() => {
				dispatch(deliveryDistanceCustomer({
					street: watchAddressStreet,
					streetNumber: watchAddressHouse,
					city: watchAddressCity,
					postalCode: watchAddressZipCode,
				}))
			}, 300)
			return () => clearTimeout(timeOutId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchAddressHouse, customerAddressIsNotFull])

	const onAutoCompletePhone = useCallback(value => {
		if (value) {
			dispatch(searchCustomersByPhoneThunk(value))
		} else {
			dispatch(setCustomersByPhone(null))
		}
	}, [dispatch])

	const filterPhone = useCallback(v => v.replace(/\D/g, '').slice(0, 20), [])

	const setItemsByPhone = useCallback(value => {
		dispatch(setCustomersByPhone(value))
	}, [dispatch])

	const onAutoCompleteCustomerNumber = useCallback(value => {
		if (value) {
			dispatch(searchCustomersByNumberThunk(value))
		} else {
			dispatch(setCustomersByNumber(null))
		}
	}, [dispatch])

	const filterNumber = useCallback(v => v.replace(/\D/g, '').slice(0, 19), [])

	const setItemsByNumber = useCallback(value => {
		dispatch(setCustomersByNumber(value))
	}, [dispatch])

	const onSubmitData = useCallback((data) => {
		onSubmit(data)
		dispatch(setDeliveryCartOrderType('DELIVERY'))
	}, [onSubmit, dispatch])

	const onBlurDeliveryCost = useCallback(() => {
		if (!watchDeliveryCost || parseInt(watchDeliveryCost) <= 0) {
			setValue('deliveryCost', 0)
		}
	}, [setValue, watchDeliveryCost])

	const handleF2 = useCallback(() => {
		dispatch(setDeliveryCartOrderType('DELIVERY'))
		handleSubmit(onSubmitData)()
	}, [onSubmitData, dispatch, handleSubmit])

	const handleEscape = useCallback(() => {
		setFocus('phoneNumber')
		handleReset()
		dispatch(setCustomerAddressIsNotFull(false))
		dispatch(setCustomer(null))
		dispatch(searchResetThunk())
		dispatch(setDeliveryCartItems([]))
		dispatch(setDeliveryCartItemFocusIndex(null))
		dispatch(setDeliveryCartCurrentDeliveryCost(0))
		dispatch(resetDeliveryConfigThunk())
	}, [handleReset, dispatch, setFocus])

	const handleShowFormClick = () => setAlwaysShowForm(!alwaysShowForm)

	const handleZerosInZipCode = useCallback((data) => {
		if (data.postalCode === '00000') {
			setShowSuggest(true)
		}
	}, [])

	const handleCustomerClick = useCallback((data) => {
		if (!data.geoData || !data.geoData.lon || !data.geoData.lat) {
			dispatch(setCustomer(data))
			dispatch(setAutocompleteGeo(true))
		} else {
			dispatch(setCustomer(data))
			dispatch(setAutocompleteGeo(false))
			if (data.geoData.distance === null) {
				dispatch(distanceCustomer(data.geoData))
			}
		}

		dispatch(searchResetThunk())
		dispatch(setAppEventZIndex(0))
		handleZerosInZipCode(data)
	}, [dispatch, handleZerosInZipCode])

	const handleShowForm = useCallback(() => {
		setShowForm(true)
	}, [])

	const formatAddressInInput = useCallback((data) => {
		return formatAddress(data.street, data.countryName, data.postalCode, data.city)
	}, [])

	const handlePhoneInputClick = useCallback((e) => {
		if (e?.stopPropagation) e.stopPropagation()
		const phoneInput = phoneInputRef?.current
		if (phoneInputMode === 'none' && phoneInput) {
			setPhoneInputMode('tel')
			phoneInput.blur()
			phoneInput.focus()
		}
	}, [phoneInputMode])

	const handlePhoneInputRef = (e) => {
		registerPhoneNumberRef(e)
		phoneInputRef.current = e
	}

	return (
		<CustomerFormWrapper>
			<CustomerMainForm id="client-form"
												data-testid="customer-form"
												onSubmit={handleSubmit(onSubmitData)}
												onFocus={handleShowForm}
												onChange={handleShowForm}
												className={!showForm ? 'toggled' : ''}>
				<FormLine ref={formRef}>
					<CustomerPhoneNumberWrapper>
						<AutocompleteInput testId="customer-phone-number"
															 name="phoneNumber"
															 tabIndex="901"
															 zIndex={PHONE_EVENT_Z_INDEX}
															 returnZIndex={EVENT_Z_INDEX}
															 autoFocus={true}
															 label={t('AddOrder.Client.form.Telefonnumer')}
															 errors={errors}
															 register={register}
															 options={{
																 required: t('app.validation.required'),
																 pattern: {
																	 value: /^[0-9]{3,15}$/i,
																	 message: t('app.validation.minNumber3'),
																 },
															 }}
															 items={customersByPhone}
															 Item={PhoneAutocompleteItem}
															 watch={watch('phoneNumber')}
															 onAutoComplete={onAutoCompletePhone}
															 filter={filterPhone}
															 setItems={setItemsByPhone}
															 onClick={handleCustomerClick}
															 onReset={handleEscape}
															 onSubmit={handleF2}
															 inputMode={phoneInputMode}
															 inputRef={handlePhoneInputRef}
															 onInputClick={handlePhoneInputClick}>
							<img src={keyboardImg} alt="keyboard" onClick={handlePhoneInputClick} />
						</AutocompleteInput>
					</CustomerPhoneNumberWrapper>
					<AutocompleteInput testId="customer-number"
														 name="customerNumber"
														 tabIndex="902"
														 disabled={customer ? customer.customerNumber : false}
														 zIndex={NUMBER_EVENT_Z_INDEX}
														 returnZIndex={EVENT_Z_INDEX}
														 label={t('AddOrder.Client.form.customerNumber')}
														 errors={errors}
														 register={register}
														 options={{
															 pattern: {
																 value: /^[0-9]{1,150}$/i,
																 message: t('app.validation.number'),
															 },
														 }}
														 items={customersByNumber}
														 Item={NumberAutocompleteItem}
														 watch={watch('customerNumber')}
														 onAutoComplete={onAutoCompleteCustomerNumber}
														 filter={filterNumber}
														 setItems={setItemsByNumber}
														 onClick={handleCustomerClick}
														 onReset={handleEscape}
														 onSubmit={handleF2}
														 inputMode="tel" />
				</FormLine>
				<CustomerNameInputs {...{ showForm, register, errors, watch, handleF2, handleEscape, handleCustomerClick, returnZIndex: EVENT_Z_INDEX }} />
				<CustomerAddressWrapper className={!showForm ? 'lf-hidden' : ''}>
					{isTestCompany ?
						<AddressSearchInputsV2 firstInputName="address"
																	 secondInputName="streetNumber"
																	 dataName="customerAddress"
																	 firstTabIndex="905"
																	 secondTabIndex="906"
																	 firstLabel={t('AddOrder.Client.form.Strasse')}
																	 secondLabel={t('AddOrder.Client.form.Hausnummer')}
																	 errors={errors}
																	 watch={watch}
																	 setValue={setValue}
																	 formatAddress={formatAddressInInput}
																	 countryDataRequired={false}
																	 autocompleteRequest={true}
																	 register={register}
																	 setFocus={setFocus}
																	 cols="two"
																	 countryCode={companyContryCode}
																	 orderType={orderType}
																	 validationIsRequired={validationIsRequired}
																	 clearErrors={clearErrors}
																	 showSuggest={showSuggest}
																	 setShowSuggest={setShowSuggest} />
						: <AddressSearchInputs firstInputName="address"
																	 secondInputName="streetNumber"
																	 dataName="customerAddress"
																	 firstTabIndex="905"
																	 secondTabIndex="906"
																	 firstLabel={t('AddOrder.Client.form.Strasse')}
																	 secondLabel={t('AddOrder.Client.form.Hausnummer')}
																	 errors={errors}
																	 watch={watch}
																	 setValue={setValue}
																	 formatAddress={formatAddressInInput}
																	 countryDataRequired={false}
																	 autocompleteRequest={true}
																	 register={register}
																	 setFocus={setFocus}
																	 cols="two"
																	 countryCode={companyContryCode}
																	 orderType={orderType}
																	 validationIsRequired={validationIsRequired}
																	 clearErrors={clearErrors} />}
					<input data-testid="customer-geodata-distance" type="hidden" value={geoData?.distance || ''} /> {/* only for tests */}
				</CustomerAddressWrapper>
				<FormLine className={!showForm ? 'lf-hidden' : ''}>
					<TextInput testId="customer-remark"
										 name="remark"
										 tabIndex="907"
										 errors={errors}
										 label={t('AddOrder.Client.form.Bemmerkung')}
										 register={register}
										 watch={watch('remark')} />
					<TextInput testId="customer-delivery-cost"
										 name="deliveryCost"
										 tabIndex="908"
										 onBlur={onBlurDeliveryCost}
										 errors={errors}
										 label={t('AddOrder.Client.form.Anfahrthosten')}
										 register={register}
										 watch={watch} />
				</FormLine>
				{!isTablet && <BlockShowForm className={!showForm ? 'lf-hidden' : ''}>
					<CheckBoxInputControlledGray label={t('AddOrder.Client.form.control')}
																			 onChange={handleShowFormClick}
																			 isActive={alwaysShowForm} />
				</BlockShowForm>}
				<FormButtons className={'justify' + (showForm ? '' : ' lf-hidden')}>
					<Button text={t('AddOrder.Client.form.Loschen')} keyButton="Escape" keyText="Esc" size={!isTablet && 'big'} zIndex={EVENT_Z_INDEX} onClick={handleEscape} />
					<Button text={t('AddOrder.Client.form.Bestellung')} color="green" keyButton="F2" size={!isTablet && 'big'} zIndex={EVENT_Z_INDEX} disabled={geoDataLoading} onClick={handleF2} type="submit" />
				</FormButtons>
			</CustomerMainForm>
		</CustomerFormWrapper>
	)
})

CustomerForm.displayName = 'CustomerForm'
export default CustomerForm
